import React, { useRef, useState } from 'react';
import { Upload, ImageIcon, Loader2, Trash2, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';

/**
 * ImageUploader — client-side compressed image upload for menu items.
 *
 * Compresses the image to max 1024px on the longest side and re-encodes it
 * as JPEG (quality 0.85) BEFORE uploading, so DB payloads stay small (typically
 * 100–350 KB per photo). Backend stores as base64 in the `images` collection
 * and serves it under `/api/images/{id}` with strong caching.
 *
 * Props:
 *   - value: current image_url ('' or '/api/images/<id>' or any URL)
 *   - onChange(url): callback with new URL (or '' when cleared)
 *   - aspect: css aspect-ratio class (default 'aspect-[4/3]')
 *   - label: label shown above the preview (optional)
 */
export const ImageUploader = ({ value, onChange, aspect = 'aspect-[4/3]', label = 'Immagine', testid = 'image-uploader' }) => {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const openGallery = () => fileRef.current?.click();
  const openCamera  = () => cameraRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine valido');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Immagine troppo grande (max 15 MB)');
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await compressImage(file, { maxDimension: 1024, quality: 0.85 });
      const res = await api.post('/admin/upload/image', {
        data_url: dataUrl,
        filename: file.name || null,
      });
      onChange?.(res.data.url);
      toast.success('Immagine caricata');
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Errore caricamento immagine');
    } finally {
      setUploading(false);
      if (fileRef.current)   fileRef.current.value   = '';
      if (cameraRef.current) cameraRef.current.value = '';
    }
  };

  const clear = () => onChange?.('');

  const hasImage = !!(value && value.trim());
  const previewSrc = hasImage ? (value.startsWith('http') || value.startsWith('data:') || value.startsWith('/api/') ? value : value) : null;

  return (
    <div className="space-y-2" data-testid={testid}>
      {label && <div className="text-sm font-medium text-[--brand-fg]">{label}</div>}
      <div className={`relative ${aspect} rounded-xl overflow-hidden border-2 border-dashed border-[--brand-border] bg-[--brand-surface-2] flex items-center justify-center`}>
        {previewSrc ? (
          <>
            <img src={previewSrc} alt="anteprima" className="w-full h-full object-cover" />
            {uploading && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <Loader2 size={28} className="animate-spin text-white" />
              </div>
            )}
            <button
              type="button"
              onClick={clear}
              className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm"
              title="Rimuovi immagine"
              data-testid="image-uploader-clear"
            >
              <Trash2 size={14} />
            </button>
          </>
        ) : uploading ? (
          <div className="flex flex-col items-center gap-2 text-[--brand-muted]">
            <Loader2 size={22} className="animate-spin"/>
            <div className="text-xs">Caricamento in corso...</div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 text-[--brand-muted]">
            <ImageIcon size={28}/>
            <div className="text-xs">Nessuna immagine</div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={openGallery} disabled={uploading} size="sm" variant="secondary" className="rounded-xl" data-testid="image-uploader-gallery">
          <Upload size={14} className="mr-1"/>{hasImage ? 'Cambia' : 'Dalla galleria'}
        </Button>
        <Button type="button" onClick={openCamera} disabled={uploading} size="sm" variant="secondary" className="rounded-xl" data-testid="image-uploader-camera">
          <Camera size={14} className="mr-1"/>Scatta foto
        </Button>
      </div>
      <input ref={fileRef}   type="file" accept="image/*"                onChange={e => handleFile(e.target.files?.[0])} className="hidden"/>
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={e => handleFile(e.target.files?.[0])} className="hidden"/>
      <p className="text-xs text-[--brand-muted]">JPG · PNG · WEBP. Verrà compressa automaticamente.</p>
    </div>
  );
};

/**
 * Compress an image file to a JPEG data URL, resizing to fit within maxDimension.
 */
async function compressImage(file, { maxDimension = 1024, quality = 0.85 } = {}) {
  const bitmap = await loadBitmap(file);
  const { width, height } = fitWithin(bitmap.width, bitmap.height, maxDimension);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  // White background for JPEG (removes transparency)
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  try { bitmap.close?.(); } catch (e) {}
  return canvas.toDataURL('image/jpeg', quality);
}

function fitWithin(w, h, maxDim) {
  const longest = Math.max(w, h);
  if (longest <= maxDim) return { width: w, height: h };
  const ratio = maxDim / longest;
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) };
}

async function loadBitmap(file) {
  if ('createImageBitmap' in window) {
    try { return await createImageBitmap(file); } catch (e) { /* fallback below */ }
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Impossibile caricare immagine')); };
    img.src = url;
  });
}

export default ImageUploader;
